import meadowUrl from './assets/meadow.webp';
import {
  ART_URL,
  DOG_ART,
  DOG_MASK,
  FRAMES,
  CHARACTER,
  CHARACTER_ART,
  NORMAL_ART,
  CHARACTER_MASKS,
  decorateStickers,
} from './art.mjs';
import {
  freshRound,
  pace,
  collect,
  expressionAt,
  chooseKind,
  crossesBasket,
  clamp,
} from './rules.mjs';

const $ = (id) => document.getElementById(id);
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
decorateStickers();
let scene;

class LittleSounds {
  constructor() {
    this.muted = false;
    try {
      this.muted = localStorage.getItem('petal-paws-muted') === 'yes';
    } catch {}
  }
  unlock() {
    if (this.muted) return;
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) return;
      this.context ??= new Audio();
      this.context.resume().catch(() => {});
    } catch {}
  }
  note(frequency, delay = 0, length = 0.18, volume = 0.045) {
    const c = this.context;
    if (this.muted || !c || c.state !== 'running') return;
    const oscillator = c.createOscillator(),
      gain = c.createGain(),
      at = c.currentTime + delay;
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, at);
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(volume, at + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, at + length);
    oscillator.connect(gain);
    gain.connect(c.destination);
    oscillator.start(at);
    oscillator.stop(at + length + 0.02);
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
    };
  }
  play(kind) {
    if (kind === 'cat') {
      this.note(784);
      this.note(1046, 0.07, 0.2, 0.03);
    } else if (kind === 'dog') {
      this.note(260, 0, 0.15, 0.04);
      this.note(220, 0.09, 0.18, 0.03);
    } else if (kind === 'start') {
      this.note(523);
      this.note(659, 0.09);
      this.note(784, 0.18);
    } else {
      this.note(440);
      this.note(349, 0.13);
      this.note(294, 0.26, 0.28);
    }
  }
  toggle() {
    this.muted = !this.muted;
    try {
      localStorage.setItem('petal-paws-muted', this.muted ? 'yes' : 'no');
    } catch {}
    this.sync();
    if (!this.muted) {
      this.unlock();
      this.note(784);
    }
  }
  sync() {
    const b = $('sound');
    b.classList.toggle('muted', this.muted);
    b.setAttribute('aria-pressed', String(this.muted));
    b.setAttribute('aria-label', this.muted ? 'Enable sounds' : 'Mute sounds');
    b.title = this.muted ? 'Enable sounds' : 'Mute sounds';
  }
}
const sounds = new LittleSounds();
sounds.sync();
$('sound').addEventListener('click', () => sounds.toggle());

function updateHUD(round) {
  $('score').textContent = String(round.score).padStart(2, '0');
  [...$('hearts').children].forEach((heart, i) =>
    heart.classList.toggle('lost', i >= round.hearts),
  );
  $('hearts').setAttribute(
    'aria-label',
    `${round.hearts} ${round.hearts === 1 ? 'heart' : 'hearts'} remaining`,
  );
  $('pause').disabled = round.status !== 'playing';
}
function setModal(name) {
  $('modal-scrim').hidden = !name;
  $('pause-panel').hidden = name !== 'pause';
  $('end-panel').hidden = name !== 'end';
  $('welcome').inert = !!name;
  document.querySelector('.hud').inert = !!name;
  document.querySelector('.touch-footer').inert = !!name;
  if (name)
    requestAnimationFrame(() =>
      $(name === 'pause' ? 'resume' : 'replay').focus({ preventScroll: true }),
    );
}
function failed() {
  $('load-note').textContent = 'The meadow didn’t load. Please try again.';
  $('play').disabled = false;
  $('play').textContent = 'Reload the meadow';
  $('play').onclick = () => location.reload();
}

function boot() {
  const Phaser = window.Phaser;
  class Meadow extends Phaser.Scene {
    constructor() {
      super('Meadow');
      this.round = { ...freshRound(), status: 'title' };
      this.items = [];
      this.sparks = [];
      this.visualTime = 0;
      this.failedLoad = false;
      this.dragId = null;
    }
    preload() {
      this.load.image('sky', meadowUrl);
      this.load.image('stickers', ART_URL);
      this.load.image(CHARACTER.texture, CHARACTER_ART.url);
      this.load.image('dog', DOG_ART.url);
      this.load.image('normal-character', NORMAL_ART.url);
      this.load.on('loaderror', () => {
        this.failedLoad = true;
        failed();
      });
    }
    create() {
      if (this.failedLoad) return;
      scene = this;
      Object.entries(FRAMES).forEach(([name, f]) =>
        this.textures.get(f.texture || 'stickers').add(name, 0, f.x, f.y, f.w, f.h),
      );
      this.sky = this.add.image(0, 0, 'sky').setOrigin(0).setDepth(-10);
      this.shadow = this.add.ellipse(0, 0, 88, 11, 0x608d78, 0.14).setDepth(1);
      this.player = this.add
        .image(0, 0, FRAMES.normal.texture, CHARACTER.normal)
        .setOrigin(0.5, 1)
        .setDepth(3);
      this.characterMasks = {};
      for (const [name, outline] of Object.entries(CHARACTER_MASKS)) {
        const frame = FRAMES[name],
          graphic = this.make.graphics({ x: 0, y: 0, add: false });
        graphic.fillStyle(0xffffff, 1).fillPoints(
          outline.map(([x, y]) => ({ x: x - frame.w * 0.5, y: y - frame.h })),
          true,
        );
        this.characterMasks[name] = { graphic, mask: graphic.createGeometryMask() };
      }
      this.setCharacterFrame(CHARACTER.normal);
      for (let i = 0; i < 12; i++) {
        const spark = this.add
          .star(0, 0, 4, 1.1, 3.6, i % 2 ? 0xffffff : 0xf5d6dc, 0.65)
          .setDepth(-1);
        this.sparks.push({
          sprite: spark,
          px: Math.random(),
          py: Math.random() * 0.67 + 0.13,
          phase: Math.random() * 7,
        });
      }
      this.layout();
      this.scale.on('resize', () => this.layout());
      this.input.on('pointerdown', (pointer) => {
        if (this.round.status !== 'playing' || this.dragId !== null) return;
        this.dragId = pointer.id;
        this.dragOffset = this.player.x - pointer.x;
      });
      this.input.on('pointermove', (pointer) => {
        if (this.round.status === 'playing' && pointer.isDown && pointer.id === this.dragId)
          this.targetX = clamp(
            pointer.x + this.dragOffset,
            this.playerMargin,
            this.w - this.playerMargin,
          );
      });
      const release = (pointer) => {
        if (pointer.id === this.dragId) this.dragId = null;
      };
      this.input.on('pointerup', release);
      this.input.on('pointerupoutside', release);
      this.input.on('gameout', () => {
        this.dragId = null;
      });
      this.keys = this.input.keyboard.addKeys({ left: 'LEFT', right: 'RIGHT' });
      this.input.keyboard.addCapture(['LEFT', 'RIGHT']);
      $('play').disabled = false;
      $('load-note').textContent = 'Missed a cat? That’s okay.';
      updateHUD(this.round);
    }
    layout() {
      const previousW = this.w || this.scale.width,
        previousH = this.h || this.scale.height;
      this.w = this.scale.width;
      this.h = this.scale.height;
      if (!this.player) return;
      const factor = Math.max(this.w / 1024, this.h / 1536);
      this.sky
        .setScale(factor)
        .setPosition((this.w - 1024 * factor) / 2, (this.h - 1536 * factor) / 2);
      this.charHeight =
        this.round.status === 'title'
          ? clamp(this.h * 0.205, 100, 150)
          : clamp(this.h * 0.245, 126, 190);
      const f = FRAMES.happy;
      this.player.setDisplaySize((this.charHeight * f.w) / f.h, this.charHeight);
      this.baseScale = this.player.scaleX;
      this.playerMargin = this.player.displayWidth * 0.5 + 6;
      const bottomInset =
        parseFloat(getComputedStyle(document.querySelector('.touch-footer')).paddingBottom) || 15;
      this.baseY = this.h - 55 - bottomInset;
      this.player.x = clamp(
        ((this.player.x || previousW * 0.5) / previousW) * this.w,
        this.playerMargin,
        this.w - this.playerMargin,
      );
      this.targetX = this.player.x;
      this.player.y = this.baseY;
      this.rimY = this.baseY - this.charHeight * CHARACTER.basketHeightRatio;
      this.halfBasket = this.player.displayWidth * CHARACTER.basketHalfWidthRatio;
      this.shadow.setPosition(this.player.x, this.baseY - 3).setScale(this.charHeight / 190);
      this.syncCharacterMask();
      for (const item of this.items) {
        item.baseX = (item.baseX / previousW) * this.w;
        item.sprite.x = (item.sprite.x / previousW) * this.w;
        item.sprite.y = (item.sprite.y / previousH) * this.h;
      }
    }
    setCharacterFrame(name) {
      this.player.setTexture(FRAMES[name].texture, name);
      this.activeCharacterMask = this.characterMasks[name];
      this.player.setMask(this.activeCharacterMask.mask);
      this.syncCharacterMask();
    }
    syncCharacterMask() {
      const graphic = this.activeCharacterMask?.graphic;
      if (graphic)
        graphic
          .setPosition(this.player.x, this.player.y)
          .setScale(this.player.scaleX, this.player.scaleY)
          .setRotation(this.player.rotation);
    }
    clearRound() {
      this.tweens.killAll();
      this.time.removeAllEvents();
      for (const item of this.items) item.sprite.destroy();
      this.items = [];
      for (const effect of this.children.list.filter((child) => child.getData?.('temporary')))
        effect.destroy();
      this.player.setAlpha(1).setAngle(0);
      this.setCharacterFrame(CHARACTER.normal);
      $('message').classList.remove('visible');
      this.dragId = null;
      this.bounceUntil = 0;
      this.firstDogSeen = false;
    }
    begin() {
      sounds.unlock();
      this.scene.resume();
      this.clearRound();
      this.round = freshRound();
      this.nextSpawn = 0.35;
      this.layout();
      this.player.x = this.w * 0.5;
      this.targetX = this.player.x;
      $('welcome').hidden = true;
      setModal(null);
      updateHUD(this.round);
      $('gesture-hint').style.opacity = '1';
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      this.toast('Catch the cats. Let the dogs pass.', 3.5);
      sounds.play('start');
      $('live-status').textContent = 'Game started. Three hearts.';
    }
    home() {
      this.scene.resume();
      this.clearRound();
      this.round = { ...freshRound(), status: 'title' };
      this.layout();
      this.player.x = this.w * 0.5;
      this.targetX = this.player.x;
      setModal(null);
      $('welcome').hidden = false;
      $('gesture-hint').style.opacity = '1';
      updateHUD(this.round);
      $('play').focus({ preventScroll: true });
    }
    pause() {
      if (this.round.status !== 'playing') return;
      this.round.status = 'paused';
      this.dragId = null;
      this.input.keyboard.resetKeys();
      updateHUD(this.round);
      setModal('pause');
      this.scene.pause();
    }
    resume() {
      if (this.round.status !== 'paused') return;
      sounds.unlock();
      this.input.keyboard.resetKeys();
      this.round.status = 'playing';
      setModal(null);
      updateHUD(this.round);
      this.scene.resume();
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
    toast(text, seconds = 2) {
      this.toastUntil = this.round.elapsed + seconds;
      $('message').textContent = text;
      $('message').classList.add('visible');
    }
    spawn() {
      const kind = chooseKind(this.round),
        frame = kind === 'dog' ? 'dog' : `cat${Phaser.Math.Between(0, 2)}`;
      const size = clamp(this.w * 0.145, 45, 61) * (kind === 'dog' ? 1.08 : 1);
      const safeEdge = Math.max(size * 0.5 + 10, this.playerMargin - this.halfBasket + 6);
      let x = Phaser.Math.FloatBetween(safeEdge, this.w - safeEdge);
      if (this.round.spawned === 1) x = this.w * 0.5;
      const f = FRAMES[frame],
        sprite = this.add
          .image(x, -size, f.texture || 'stickers', frame)
          .setDisplaySize(size, (size * f.h) / f.w)
          .setDepth(4);
      if (kind === 'dog') {
        const graphic = this.make.graphics({ x: sprite.x, y: sprite.y, add: false });
        graphic.fillStyle(0xffffff, 1).fillPoints(
          DOG_MASK.map(([x, y]) => ({ x: x - f.w * 0.5, y: y - f.h * 0.5 })),
          true,
        );
        graphic.setScale(sprite.scaleX, sprite.scaleY);
        sprite.setMask(graphic.createGeometryMask());
        sprite.cutoutGraphic = graphic;
        sprite.once('destroy', () => {
          sprite.clearMask(true);
          graphic.destroy();
        });
      }
      const p = pace(this.round.elapsed);
      const travelDistance = Math.max(140, this.rimY);
      const speed = p.fallSpeed * clamp(travelDistance / 530, 0.48, 1.2);
      this.items.push({
        sprite,
        kind,
        baseX: x,
        radius: sprite.displayHeight * 0.41,
        speed,
        phase: Math.random() * 6,
      });
      if (kind === 'dog' && !this.firstDogSeen) {
        this.firstDogSeen = true;
        this.toast('Grumpy dog ahead. Let it pass!', 2.8);
      }
    }
    catch(item) {
      const result = collect(this.round, item.kind),
        x = item.sprite.x,
        y = this.rimY;
      item.sprite.destroy();
      this.items.splice(this.items.indexOf(item), 1);
      if (result === 'ignored') return;
      updateHUD(this.round);
      if (result === 'cat') {
        this.bounceUntil = this.round.elapsed + 0.3;
        this.setCharacterFrame(CHARACTER.happy);
        this.burst(x, y);
        sounds.play('cat');
        if (this.round.score === 1) this.toast('Your first little friend!');
        else if (this.round.score % 10 === 0)
          this.toast(`${this.round.score} cats. A very happy basket!`);
        return;
      }
      this.setCharacterFrame(CHARACTER.sad);
      sounds.play(result === 'ended' ? 'end' : 'dog');
      $('live-status').textContent =
        result === 'ended'
          ? `Game over. ${this.round.score} cats caught.`
          : `Dog caught. ${this.round.hearts} hearts remaining.`;
      if (result === 'ended') {
        this.player.setAlpha(1);
        this.dragId = null;
        $('final-score').textContent = this.round.score;
        $('message').classList.remove('visible');
        setModal('end');
        return;
      }
      this.toast(
        this.round.hearts === 2
          ? 'Oops, a puppy! Two hearts left.'
          : 'One heart left. You’ve got this.',
      );
    }
    burst(x, y) {
      const label = this.add
        .text(x, y - 12, '+1', {
          fontFamily: 'Nunito, sans-serif',
          fontSize: '25px',
          fontStyle: 'bold',
          color: '#ca688f',
          stroke: '#fffdf7',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setDepth(8)
        .setData('temporary', true);
      this.tweens.add({
        targets: label,
        y: y - 50,
        alpha: 0,
        duration: reducedMotion ? 300 : 680,
        ease: 'Sine.easeOut',
        onComplete: () => label.destroy(),
      });
      if (reducedMotion) return;
      for (let i = 0; i < 7; i++) {
        const angle = (i / 7) * Math.PI * 2,
          star = this.add
            .star(x, y, 4, 2, 5, i % 2 ? 0xffd18b : 0xf4a7be)
            .setDepth(7)
            .setData('temporary', true);
        this.tweens.add({
          targets: star,
          x: x + Math.cos(angle) * Phaser.Math.Between(20, 44),
          y: y + Math.sin(angle) * 27 - 20,
          alpha: 0,
          scale: 0,
          angle: 80,
          duration: 550,
          onComplete: () => star.destroy(),
        });
      }
    }
    update(_, delta) {
      if (!this.player) return;
      const dt = Math.min(delta, 50) / 1000;
      this.visualTime += dt;
      if (!reducedMotion)
        for (const s of this.sparks) {
          s.sprite.setPosition(
            s.px * this.w + Math.sin(this.visualTime * 0.3 + s.phase) * 9,
            s.py * this.h + Math.sin(this.visualTime * 0.35 + s.phase) * 7,
          );
          s.sprite.alpha = 0.22 + (0.5 + 0.5 * Math.sin(this.visualTime + s.phase)) * 0.38;
        }
      if (this.round.status === 'title') {
        this.player.y = this.baseY + (reducedMotion ? 0 : Math.sin(this.visualTime * 1.7) * 2.3);
        this.syncCharacterMask();
        return;
      }
      if (this.round.status !== 'playing') return;
      this.round.elapsed += dt;
      const oldBasketX = this.player.x;
      if (this.keys.left.isDown) this.targetX -= this.w * 1.4 * dt;
      if (this.keys.right.isDown) this.targetX += this.w * 1.4 * dt;
      this.targetX = clamp(this.targetX, this.playerMargin, this.w - this.playerMargin);
      const movement = (this.targetX - this.player.x) * (1 - Math.exp(-18 * dt));
      this.player.x += clamp(movement, -this.w * 2.2 * dt, this.w * 2.2 * dt);
      this.player.y = this.baseY;
      this.shadow.x = this.player.x;
      if (!reducedMotion) {
        this.player.angle = clamp(movement * 0.35, -3, 3);
        if (this.round.elapsed < this.bounceUntil)
          this.player.y -=
            Math.sin(((0.3 - (this.bounceUntil - this.round.elapsed)) / 0.3) * Math.PI) * 4;
      }
      this.player.alpha =
        !reducedMotion && this.round.elapsed < this.round.graceUntil
          ? 0.65 + 0.35 * Math.abs(Math.sin(this.round.elapsed * 8))
          : 1;
      const expression = CHARACTER[expressionAt(this.round)];
      if (this.player.frame.name !== expression) this.setCharacterFrame(expression);
      this.syncCharacterMask();
      if (this.round.elapsed >= this.toastUntil) $('message').classList.remove('visible');
      if (this.round.elapsed > 10) $('gesture-hint').style.opacity = '.45';
      if (this.round.elapsed >= this.nextSpawn) {
        this.spawn();
        this.nextSpawn =
          this.round.elapsed + pace(this.round.elapsed).spawnInterval + Math.random() * 0.2;
      }
      for (const item of [...this.items]) {
        const oldY = item.sprite.y,
          oldX = item.sprite.x;
        item.sprite.y += item.speed * dt;
        item.sprite.x =
          item.baseX + (reducedMotion ? 0 : Math.sin(this.round.elapsed * 1.8 + item.phase) * 5);
        item.sprite.angle = reducedMotion ? 0 : Math.sin(this.round.elapsed * 1.6 + item.phase) * 7;
        if (item.sprite.cutoutGraphic)
          item.sprite.cutoutGraphic
            .setPosition(item.sprite.x, item.sprite.y)
            .setRotation(item.sprite.rotation);
        if (
          crossesBasket(
            oldY,
            item.sprite.y,
            item.radius,
            oldX,
            item.sprite.x,
            oldBasketX,
            this.player.x,
            this.rimY,
            this.halfBasket,
          )
        ) {
          this.catch(item);
          if (this.round.status === 'ended') break;
        } else if (item.sprite.y - item.radius > this.h) {
          item.sprite.destroy();
          this.items.splice(this.items.indexOf(item), 1);
        }
      }
    }
  }
  new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'arena',
    transparent: true,
    antialias: true,
    powerPreference: 'low-power',
    resolution: Math.min(devicePixelRatio || 1, 2),
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: $('arena').clientWidth,
      height: $('arena').clientHeight,
    },
    input: { activePointers: 1 },
    audio: { noAudio: true },
    scene: Meadow,
    banner: false,
  });
}

$('play').addEventListener('click', () => scene?.begin());
$('pause').addEventListener('click', () => scene?.pause());
$('resume').addEventListener('click', () => scene?.resume());
$('restart-paused').addEventListener('click', () => scene?.begin());
$('replay').addEventListener('click', () => scene?.begin());
$('home').addEventListener('click', () => scene?.home());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) scene?.pause();
});
window.addEventListener('blur', () => scene?.pause());
window.addEventListener('keydown', (event) => {
  if ((event.code === 'Space' || event.code === 'Escape') && scene) {
    if (event.code === 'Space' && document.activeElement?.tagName === 'BUTTON') return;
    event.preventDefault();
    if (scene.round.status === 'playing') scene.pause();
    else if (scene.round.status === 'paused') scene.resume();
  }
  if (event.key === 'Tab' && !$('modal-scrim').hidden) {
    const panel = $('pause-panel').hidden ? $('end-panel') : $('pause-panel'),
      buttons = [...panel.querySelectorAll('button')],
      first = buttons[0],
      last = buttons.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
});
if (window.Phaser) {
  try {
    boot();
  } catch (error) {
    console.error(error);
    failed();
  }
} else failed();
